import React, {
  createContext,
  useContext,
  useCallback,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react'
import { useNavigate } from 'react-router-dom'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import llmChatUpdateDefinition from '~/packages/llmChat/update/llmChatUpdate.definition'
import { toastError, toastInfo, toastSuccess } from '~/components/toasts'
import { Chat } from './chatCard'

interface LlmChatContextType {
  onArchiveChat: (chatId: number) => Promise<boolean | undefined>
  onRestoreChat: (chatId: number) => Promise<boolean | undefined>
  onRenameChat: (chatId: number, newTitle: string) => Promise<boolean | undefined>
  onFixChat: (chatId: number, isFixed: boolean) => Promise<boolean | undefined>
  refreshChatList: () => Promise<void>
  chatList: Chat[]
  isLoadingChatList: boolean
}

export const MAX_FIXED_CHATS = 3

const LlmChatContext = createContext<LlmChatContextType | undefined>(undefined)

export function LlmChatProvider({
  children,
  currentChatId,
  setChatList,
  chatList,
  handleChangeListType,
  refreshChatList,
  isLoadingChatList,
}: {
  children: React.ReactNode
  currentChatId?: number
  setChatList: Dispatch<SetStateAction<any[]>>
  chatList: any[]
  handleChangeListType: (type: 'active' | 'archived') => void
  refreshChatList: () => Promise<void>
  isLoadingChatList: boolean
}) {
  const navigate = useNavigate()

  const { callAction: updateChat } = useActionFunction(llmChatUpdateDefinition, {
    initialIsLoading: false,
  })

  const numberOfFixedChats = useMemo(() => {
    return chatList.filter(chat => chat.isFixed).length
  }, [chatList])

  const onArchiveChat = useCallback(
    async (chatId: number) => {
      const { resultPromise } = updateChat({
        id: chatId,
        archivedAt: new Date().toISOString(),
        isFixed: false,
      })
      const result = await resultPromise

      if (result) {
        if (currentChatId && chatId === currentChatId) {
          navigate('/app/ai')
        }

        setChatList(prev => prev.filter(chat => chat.id !== chatId))
        toastSuccess('Chat arquivado com sucesso')
        return true
      }

      toastError('Ocorreu um erro ao arquivar o chat')
      return false
    },
    [updateChat, currentChatId, navigate, setChatList],
  )

  const onRenameChat = useCallback(
    async (chatId: number, newTitle: string) => {
      if (newTitle.length < 3) {
        toastInfo('O título do chat deve ter pelo menos 3 caracteres')
        return false
      }

      const { resultPromise } = updateChat({ id: chatId, title: newTitle })
      const result = await resultPromise

      if (result) {
        setChatList(prev =>
          prev.map(chat => (chat.id === chatId ? { ...chat, title: newTitle } : chat)),
        )
        toastSuccess(`Chat renomeado com sucesso`)

        return true
      }

      toastError(`Ocorreu um erro ao renomear o chat`)
      return false
    },
    [updateChat, setChatList],
  )

  const onFixChat = useCallback(
    async (chatId: number, isFixed: boolean) => {
      if (isFixed && numberOfFixedChats >= MAX_FIXED_CHATS) {
        toastInfo('Você atingiu o limite de chats fixados')
        return false
      }

      const { resultPromise } = updateChat({ id: chatId, isFixed: isFixed })
      const result = await resultPromise

      if (result) {
        setChatList(prev => prev.map(chat => (chat.id === chatId ? { ...chat, isFixed } : chat)))
        toastSuccess(`Chat ${isFixed ? 'fixado' : 'desafixado'} com sucesso`)

        return true
      }

      toastError(`Ocorreu um erro ao ${isFixed ? 'fixar' : 'desafixar'} o chat`)
      return false
    },
    [updateChat, setChatList, numberOfFixedChats],
  )

  const onRestoreChat = useCallback(
    async (chatId: number) => {
      const { resultPromise } = updateChat({ id: chatId, archivedAt: null, isFixed: false })
      const result = await resultPromise

      if (result) {
        setChatList(prev => {
          const newList = prev.filter(chat => chat.id !== chatId)

          if (currentChatId === chatId) {
            if (newList.length > 0) {
              navigate(`/app/ai/archived/${newList[0]?.id}`)
            }
            if (newList.length === 0) {
              handleChangeListType('active')
            }
          }
          return newList
        })

        toastSuccess(`Chat restaurado com sucesso`)
        return true
      }

      toastError(`Ocorreu um erro ao restaurar o chat`)
      return false
    },
    [updateChat, setChatList, handleChangeListType, currentChatId, navigate],
  )

  return (
    <LlmChatContext.Provider
      value={{
        onArchiveChat,
        onRenameChat,
        onFixChat,
        onRestoreChat,
        refreshChatList,
        chatList,
        isLoadingChatList,
      }}
    >
      {children}
    </LlmChatContext.Provider>
  )
}

export function useLlmChatContext() {
  const context = useContext(LlmChatContext)
  if (!context) {
    // Return a safe fallback to prevent hard crashes when provider isn't mounted
    return {
      onArchiveChat: async () => false,
      onRestoreChat: async () => false,
      onRenameChat: async () => false,
      onFixChat: async () => false,
      refreshChatList: async () => {},
      chatList: [],
      isLoadingChatList: false,
    } as LlmChatContextType
  }
  return context
}
